import { onboardingDetails } from '../lib/constants.tsx';
import type {
  OnboardingQuestion,
  OnboardingQuestionSummary,
  OnboardingSummary,
} from '../types/onboarding';
import {
  ArcheryMatch,
  Bookmark,
  Community,
  DirectorChair,
  Suitcase,
} from 'iconoir-react';

export const onboardingExtraQuestions: Record<string, OnboardingQuestion> = {
  resume: {
    id: 'resume',
    section_id: 'resume',
    prompt: 'Upload your resume',
    is_required: true,
    is_active: true,
    code: 'resume',
    narration: null,
    description: null,
    input_type: 'text',
    options: null,
    suggestion_chips: null,
    display_order: 1,
    has_nested_question: false,
    nested_question: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  linkedin_profile_link: {
    id: 'linkedin_profile_link',
    section_id: 'linkedin_profile_link',
    prompt: 'Enter your LinkedIn profile link',
    is_required: true,
    is_active: true,
    code: 'linkedin_profile_link',
    narration: null,
    description: null,
    input_type: 'text',
    options: null,
    suggestion_chips: null,
    display_order: 1,
    has_nested_question: false,
    nested_question: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  brief_professional_background: {
    id: 'brief_professional_background',
    section_id: 'brief_professional_background',
    prompt: 'Write your brief professional background',
    is_required: true,
    is_active: true,
    code: 'brief_professional_background',
    narration: null,
    description: null,
    input_type: 'text',
    options: null,
    suggestion_chips: null,
    display_order: 1,
    has_nested_question: false,
    nested_question: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
};

export const getQuestionPlaceholder = (code: string) => {
  return onboardingDetails[code as keyof typeof onboardingDetails].placeholder;
};

export const getQuestionHelperText = (code: string) => {
  return onboardingDetails[code as keyof typeof onboardingDetails].helperText;
};

export const getSectionIcon = (code: string) => {
  if (code === 'identity') {
    return Suitcase;
  } else if (code === 'core_objectives') {
    return DirectorChair;
  } else if (code === 'ideal_connections') {
    return ArcheryMatch;
  } else if (code === 'style_prefs') {
    return Community;
  } else {
    return Bookmark;
  }
};

export const getAnswerByCode = (
  code: string,
  questions: OnboardingQuestionSummary[]
): string | string[] | Record<string, any> => {
  return questions.find(question => question.code === code)?.user_response as
    | string
    | string[]
    | Record<string, any>;
};

export const getLastActiveQuestion = (
  section: string,
  questions: OnboardingQuestion[]
): OnboardingQuestion | undefined => {
  return [...questions]
    .reverse()
    .find(q => q.section_id === section && q.is_active);
};

export function groupQuestionsBySection(
  questions: OnboardingQuestionSummary[]
): OnboardingSummary[] {
  const sectionMap = new Map<string, OnboardingSummary>();

  for (const question of questions) {
    const sectionId = question.section_id;
    const sectionCode = question.section_code;

    if (!sectionMap.has(sectionId)) {
      sectionMap.set(sectionId, {
        section_id: sectionId,
        section_code: sectionCode,
        questions: [],
      } as unknown as OnboardingSummary);
    }

    sectionMap.get(sectionId)!.questions.push(question);
  }

  return Array.from(sectionMap.values());
}

export function extractLinkedInUrl(text: string): string | null {
  if (!text) return null;

  const linkedInRegex =
    /(https?:\/\/)?(www\.)?linkedin\.com\/(in|company|pub)\/[a-zA-Z0-9-_%]+/i;

  const match = text.match(linkedInRegex);

  if (!match) return null;

  // Ensure URL always starts with https://
  const url = match[0].startsWith('http') ? match[0] : `https://${match[0]}`;

  return url;
}

export function isOnlyUrl(text: string): boolean {
  if (!text) return false;

  const trimmed = text.trim();

  const urlRegex =
    /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})(\/\S*)?$/;

  return urlRegex.test(trimmed);
}

